import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ChangePasswordModal } from '@/features/auth/ChangePasswordModal';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/Badge';

import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { Icon } from '@/shared/ui/Icon';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

const planLabels: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

export function Topbar({
  title,
  onOpenMenu,
}: {
  title: string;
  onOpenMenu: () => void;
}) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    // Fechar com a tecla de escape faz parte de qualquer sobreposicao. Sem isso,
    // quem navega por teclado fica preso no menu aberto.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const user = session?.user;
  const tenant = session?.tenant;

  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface/85 px-4 backdrop-blur-sm sm:px-6">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Abrir menu"
        className="flex size-9 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 lg:hidden"
      >
        <Icon name="menu" />
      </button>

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-[0.9375rem] font-semibold text-ink-900">
          {title}
        </h1>
        {tenant ? (
          <p className="truncate text-xs text-ink-500">
            {tenant.name}
            {/* A corretora da sessao (P-USU-1), como o BrokerName do OnPoint: quem
                tem escopo de corretora ve por qual corretora esta trabalhando. */}
            {session?.broker ? ` - ${session.broker.legal_name}` : null}
            {/* A assessoria (P-ACE-2): por qual corretora o assessor atua. */}
            {session?.advisor
              ? ` - Assessoria ${session.advisor.name}${session.advisor.acting_broker ? '' : ' (escolha a corretora)'}`
              : null}
          </p>
        ) : null}
      </div>

      {tenant ? (
        <Badge tone="brand" className="hidden sm:inline-flex">
          {planLabels[tenant.plan] ?? tenant.plan}
        </Badge>
      ) : null}

      <WorkspaceSwitcher />

      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          // Sem rotulo, o leitor de tela anuncia as iniciais como se fossem uma
          // palavra e nao diz o que o botao faz. O nome fica visivel do lado, mas
          // em tela pequena ele some e sobrariam apenas as iniciais.
          aria-label="Abrir menu da conta"
          className={cn(
            'flex items-center gap-2 rounded-full border border-line py-1 pr-3 pl-1',
            'transition-colors duration-(--duration-fast) hover:bg-ink-50',
          )}
        >
          <span
            aria-hidden="true"
            className="brand-gradient flex size-7 items-center justify-center rounded-full text-[0.6875rem] font-semibold text-white"
          >
            {initials(user?.name ?? '')}
          </span>
          <span className="hidden max-w-[10rem] truncate text-xs font-medium text-ink-700 sm:inline">
            {user?.name}
          </span>
        </button>

        {menuOpen ? (
          <div
            role="menu"
            className="surface-card absolute right-0 z-30 mt-2 w-60 p-1.5 shadow-(--shadow-lg)"
          >
            <div className="px-2.5 py-2">
              <p className="truncate text-xs font-semibold text-ink-900">
                {user?.name}
              </p>
              <p className="truncate text-2xs text-ink-500">{user?.email}</p>
            </div>
            <div className="my-1 border-t border-line" />
            {/* O caminho so aparece para quem a sessao diz que administra a
                plataforma. Isso e orientacao: o servidor recusa cada rota de /admin
                para quem nao tem a concessao, com ou sem este item no menu. */}
            {/* A troca de corretora do assessor (P-ACE-2). */}
            {session?.advisor ? (
              <Link
                to="/assessoria/corretoras"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-ink-700 hover:bg-ink-100"
              >
                <Icon name="users" size={15} />
                Trocar de corretora
              </Link>
            ) : null}
            {session?.platform_admin ? (
              <Link
                to="/admin"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-ink-700 hover:bg-ink-100"
              >
                <Icon name="shield" size={15} />
                Administracao da plataforma
              </Link>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setTrocandoSenha(true);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-ink-700 hover:bg-ink-100"
            >
              <Icon name="settings" size={15} />
              Alterar senha
            </button>
            {/* Bloquear encerra a sessao no servidor e pede a senha de novo, voltando
                para a mesma tela. E o /locked do OnPoint. */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void navigate('/sessao-bloqueada', {
                  state: { from: location.pathname },
                });
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-ink-700 hover:bg-ink-100"
            >
              <Icon name="lock" size={15} />
              Bloquear sessao
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void signOut();
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-ink-700 hover:bg-ink-100"
            >
              <Icon name="logout" size={15} />
              Encerrar sessao
            </button>
          </div>
        ) : null}
      </div>

      <ChangePasswordModal
        open={trocandoSenha}
        onClose={() => setTrocandoSenha(false)}
      />
    </header>
  );
}
